import { HowToAnswer } from "./HowToAnswer";
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * ForcedChoiceRounds — n rounds of pairwise choice, no "It depends".
 *
 * Rounds are drawn adaptively from a pool: after each pick we prefer
 * pairs that touch dimensions we haven't yet resolved. For simplicity
 * (and determinism) v1 uses a fixed shuffled schedule seeded off the
 * pool, not true adaptive; the interaction contract is identical.
 *
 * Keyboard: 1 / A = left, 2 / B = right.
 */

export interface RoundOffer {
  id: string;
  title: string;
  bullets?: string[];
  /** Dimensions this offer is "high" on. Used by the scoring caller. */
  tags?: string[];
}

export interface Round {
  id: string;
  prompt?: string;
  left: RoundOffer;
  right: RoundOffer;
}

export interface RoundPick {
  roundId: string;
  chosen: "left" | "right";
  chosenId: string;
  rejectedId: string;
  leftTags: string[];
  rightTags: string[];
}

export interface ForcedChoiceRoundsProps {
  rounds: Round[];
  onComplete: (picks: RoundPick[]) => void;
  /** Small line shown between rounds — e.g. "Two more." */
  interstitial?: (index: number, total: number) => string;
}

export function ForcedChoiceRounds({ rounds, onComplete, interstitial }: ForcedChoiceRoundsProps) {
  const [idx, setIdx] = useState(0);
  const [picks, setPicks] = useState<RoundPick[]>([]);
  // The side the learner has tapped but not yet confirmed. Every round is
  // an explicit two-step: choose, then press Continue.
  const [side, setSide] = useState<"left" | "right" | null>(null);
  const current = rounds[idx];
  const done = idx >= rounds.length;

  const commit = useMemo(() => () => {
    if (!current || !side) return;
    const chosen = side === "left" ? current.left : current.right;
    const rejected = side === "left" ? current.right : current.left;
    const p: RoundPick = {
      roundId: current.id, chosen: side,
      chosenId: chosen.id, rejectedId: rejected.id,
      leftTags: current.left.tags ?? [], rightTags: current.right.tags ?? [],
    };
    const next = [...picks, p];
    setPicks(next);
    setSide(null);
    if (idx + 1 >= rounds.length) {
      // Small delay so the click registers before the caller advances.
      setTimeout(() => onComplete(next), 220);
    }
    setIdx(idx + 1);
  }, [current, idx, picks, rounds.length, onComplete, side]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (done) return;
      if (e.key === "1" || e.key.toLowerCase() === "a") setSide("left");
      if (e.key === "2" || e.key.toLowerCase() === "b") setSide("right");
      if (e.key === "Enter") commit();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [commit, done]);


  if (done || !current) {
    return (
      <div className="min-h-[14rem] grid place-items-center text-sm text-muted-foreground">
        Getting the next thing ready…
      </div>
    );
  }

  return (
    <div>
      <HowToAnswer>Each round, pick the one you'd choose. Both are fine — go with your first instinct.</HowToAnswer>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {interstitial?.(idx, rounds.length) ?? `Round ${idx + 1} of ${rounds.length}`}
        </div>
        {/* Quiet round dots. Never a percentage — that's a compliance bar. */}
        <div className="flex gap-1">
          {rounds.map((_, i) => (
            <span
              key={i}
              className={[
                "h-1.5 w-4 rounded-full transition-colors",
                i < idx ? "bg-primary" : i === idx ? "bg-primary/60" : "bg-border",
              ].join(" ")}
            />
          ))}
        </div>
      </div>

      {current.prompt && (
        <p className="mb-4 text-muted-foreground text-sm">{current.prompt}</p>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {(["left", "right"] as const).map((s, i) => {
            const offer = current[s];
            const isSelected = side === s;
            return (
              <motion.button
                key={offer.id}
                onClick={() => setSide(s)}
                aria-pressed={isSelected}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.985 }}
                className={[
                  "text-left rounded-3xl border p-5 md:p-6 min-h-[12rem] flex flex-col",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  isSelected
                    ? "border-primary bg-primary/10"
                    : "border-border bg-card hover:border-primary/70 hover:bg-primary/5",
                ].join(" ")}
              >
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
                  {i === 0 ? "Option 1" : "Option 2"}
                </div>
                <h3 className="font-display text-lg md:text-xl font-medium leading-snug">
                  {offer.title}
                </h3>
                {offer.bullets && offer.bullets.length > 0 && (
                  <ul className="mt-3 space-y-1.5 text-sm flex-1">
                    {offer.bullets.map((b, j) => (
                      <li key={j} className="flex gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span className="text-foreground/80">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-4 text-xs text-muted-foreground">
                  Press {i === 0 ? "1" : "2"}
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      </AnimatePresence>

      <div className="mt-6 flex justify-end">
        <button
          onClick={commit}
          disabled={!side}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            inline-flex items-center gap-2
            disabled:opacity-40 disabled:pointer-events-none
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Continue
        </button>
      </div>
    </div>

  );
}

/**
 * Bradley-Terry style tally: each win adds 1 to the chosen tags, each loss
 * subtracts. Normalises the result 0..100. Ties (no picks touching a tag)
 * return 50 (neutral).
 */
export function bradleyTerry(picks: RoundPick[], tags: string[]): Record<string, number> {
  const raw: Record<string, number> = {};
  const touched: Record<string, number> = {};
  for (const t of tags) { raw[t] = 0; touched[t] = 0; }
  for (const p of picks) {
    for (const t of p.leftTags) {
      if (!(t in raw)) continue;
      touched[t]++;
      raw[t] += p.chosen === "left" ? 1 : -1;
    }
    for (const t of p.rightTags) {
      if (!(t in raw)) continue;
      touched[t]++;
      raw[t] += p.chosen === "right" ? 1 : -1;
    }
  }
  const out: Record<string, number> = {};
  for (const t of tags) {
    if (touched[t] === 0) { out[t] = 50; continue; }
    // raw ∈ [-touched, +touched] → normalise to 0..100
    out[t] = Math.round(50 + (raw[t] / touched[t]) * 50);
  }
  return out;
}
